import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { modeloDaAnamnese, pendencias, progresso, type RespostasDaAnamnese } from "@bookdental/schemas";
import { Button, FormularioDeAnamnese } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";

/**
 * A equipe preenche a anamnese com o paciente na clínica. Salva sozinho (rascunho) e, ao concluir,
 * fica registrada em nome de quem preencheu — a assinatura simples é da equipe.
 */
export function EditorNaClinica({ anamneseId, onVoltar }: { anamneseId: string; onVoltar: () => void }) {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const q = trpc.anamnese.obter.useQuery({ id: anamneseId });
  const [respostas, setRespostas] = useState<RespostasDaAnamnese | null>(null);
  const [pendentes, setPendentes] = useState<Set<string>>(new Set());
  const sujo = useRef(false);
  const invalidar = () => {
    utils.anamnese.listar.invalidate();
    utils.anamnese.alertasPorPacientes.invalidate();
  };
  const salvar = trpc.anamnese.salvar.useMutation({ onError: (e) => toast.error(e.message) });
  const concluir = trpc.anamnese.concluirNaClinica.useMutation({
    onSuccess: () => {
      invalidar();
      toast.success("Anamnese concluída. Falta a revisão do dentista.");
      onVoltar();
    },
    onError: (e) => toast.error(e.message),
  });

  useEffect(() => {
    if (q.data && respostas === null) setRespostas(q.data.respostas);
  }, [q.data, respostas]);
  useEffect(() => {
    if (!respostas || !sujo.current) return;
    const t = setTimeout(() => {
      sujo.current = false;
      salvar.mutate({ id: anamneseId, respostas }, { onSuccess: invalidar });
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respostas]);

  if (!q.data || !respostas) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }
  const modelo = modeloDaAnamnese(q.data.modelo, q.data.versaoDoModelo);
  const tentarConcluir = async () => {
    const faltam = pendencias(modelo, respostas);
    if (faltam.length) {
      setPendentes(new Set(faltam.map((p) => p.pergunta)));
      return toast.error(`Faltam ${faltam.length} resposta(s) obrigatória(s) — estão marcadas em amarelo.`);
    }
    if (!(await confirm({ title: "Concluir a anamnese?", description: "Ela fica registrada como preenchida por você na clínica.", confirmText: "Concluir", cancelText: "Voltar" }))) return;
    concluir.mutate({ id: anamneseId, respostas });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onVoltar} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800">
          <ArrowLeft className="h-4 w-4" /> Anamneses
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-zinc-500">
            {progresso(modelo, respostas)}% respondido{salvar.isPending ? " · salvando…" : ""}
          </span>
          <Button onClick={tentarConcluir} disabled={concluir.isPending}>
            {concluir.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Concluir anamnese
          </Button>
        </div>
      </div>
      {modelo.rascunhoClinico && (
        <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">Questionário padrão em validação clínica.</p>
      )}
      <div className="mx-auto max-w-3xl">
        <FormularioDeAnamnese
          modelo={modelo}
          respostas={respostas}
          pendentes={pendentes}
          onChange={(r) => {
            sujo.current = true;
            setRespostas(r);
          }}
        />
      </div>
    </div>
  );
}
